/**
 * Knockout bracket structure and propagation (M73 -> M104), data-driven from
 * src/data/bracket.json (extracted legacy tree: winnerTo/loserTo links).
 */
import type { Score, TeamCode } from "./types.ts";

export type Slot = "a" | "b";

export interface BracketNode {
  readonly round: string;
  readonly winnerTo: readonly [string, Slot] | null;
  readonly loserTo: readonly [string, Slot] | null;
  readonly hostCountry: string | null;
}

export type BracketNodes = Readonly<Record<string, BracketNode>>;

/**
 * Round-of-32 pairings from group ranks + thirds assignment.
 * `sources` tokens come from the legacy R32MAP: "1A"/"2B" (group rank) or
 * "3:Mxx" (third-placed team assigned to that slot).
 */
export function buildR32(
  sources: Readonly<Record<string, readonly [string, string]>>,
  ranksByGroup: Readonly<Record<string, readonly TeamCode[]>>,
  thirdsBySlot: Readonly<Record<string, string>>,
): Record<string, readonly [TeamCode, TeamCode]> {
  const resolve = (matchId: string, token: string): TeamCode => {
    if (token.startsWith("3")) {
      const slot = token.includes(":") ? token.slice(token.indexOf(":") + 1) : matchId;
      const group = thirdsBySlot[slot];
      const team = group !== undefined ? ranksByGroup[group]?.[2] : undefined;
      if (team === undefined) throw new Error(`buildR32: unresolved third for ${matchId} (${token})`);
      return team;
    }
    const rank = Number(token[0]);
    const group = token.slice(1);
    const team = ranksByGroup[group]?.[rank - 1];
    if (team === undefined) throw new Error(`buildR32: unresolved token ${token} for ${matchId}`);
    return team;
  };
  const out: Record<string, readonly [TeamCode, TeamCode]> = {};
  for (const [matchId, [a, b]] of Object.entries(sources)) {
    out[matchId] = [resolve(matchId, a), resolve(matchId, b)];
  }
  return out;
}

export interface KoMatchResult {
  /** Score at the end of play (after extra time if played). */
  readonly score: Score;
  /** Winning slot when the score is level (shoot-out). */
  readonly penaltiesWinner?: Slot;
}

export interface ResolvedKoMatch {
  readonly id: string;
  readonly a: TeamCode | null;
  readonly b: TeamCode | null;
  readonly result: KoMatchResult | null;
  readonly winner: TeamCode | null;
  readonly loser: TeamCode | null;
}

/**
 * Propagate teams through the knockout tree given R32 pairings and per-match
 * results. Unplayed matches keep null teams/winners — callers render
 * placeholders for those. Pure; results keyed by match id ("M73"...).
 */
export function resolveBracket(
  nodes: BracketNodes,
  r32Teams: Readonly<Record<string, readonly [TeamCode, TeamCode]>>,
  results: Readonly<Record<string, KoMatchResult | undefined>>,
): Record<string, ResolvedKoMatch> {
  const slots: Record<string, { a: TeamCode | null; b: TeamCode | null }> = {};
  for (const id of Object.keys(nodes)) {
    slots[id] = { a: r32Teams[id]?.[0] ?? null, b: r32Teams[id]?.[1] ?? null };
  }
  const out: Record<string, ResolvedKoMatch> = {};
  // Nodes are stored in M73..M104 order; a numeric sort keeps us safe anyway.
  const ordered = Object.keys(nodes).sort((x, y) => Number(x.slice(1)) - Number(y.slice(1)));
  for (const id of ordered) {
    const node = nodes[id];
    const teams = slots[id];
    if (node === undefined || teams === undefined) continue;
    const result = results[id] ?? null;
    let winner: TeamCode | null = null;
    let loser: TeamCode | null = null;
    if (result && teams.a !== null && teams.b !== null) {
      const [ga, gb] = result.score;
      const winSlot: Slot | null = ga > gb ? "a" : gb > ga ? "b" : (result.penaltiesWinner ?? null);
      if (winSlot !== null) {
        winner = winSlot === "a" ? teams.a : teams.b;
        loser = winSlot === "a" ? teams.b : teams.a;
        if (node.winnerTo) {
          const [target, slot] = node.winnerTo;
          const t = slots[target];
          if (t) t[slot] = winner;
        }
        if (node.loserTo) {
          const [target, slot] = node.loserTo;
          const t = slots[target];
          if (t) t[slot] = loser;
        }
      }
    }
    out[id] = { id, a: teams.a, b: teams.b, result, winner, loser };
  }
  return out;
}
